import { useCallback, useEffect, useMemo, useState } from "react";

import {
  CategoryStatus,
  type Cluster,
  ClusterDiscoveryStatus,
  type ClusterWithCloudConnect,
  type EarlyAccessFeature,
  IntegrationModel,
  type KubernetesFeature,
} from "@doitintl/cmp-models";
import { getCollection, type WithModel } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";

import { useApiContext } from "../../api/context";
import { useCloudConnect } from "../../Components/hooks/useCloudConnect";
import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { getCachingKeys } from "../../utils/cachingKeys";
import { consoleErrorWithSentry, useErrorSnackbar } from "../Integrations/Slack/utils";
import { useAWSAccounts } from "../Settings/AWS/hooks";
import { AccountStatus, AWSFeatureName, FeatureDetails } from "../Settings/AWS/types";
import { useCloudConnectData } from "../Settings/GCP/useCloudConnectData";
import { customerHasGKECluster, fetchClusterMetrics, fetchDashboardClusters, fetchDashboardWorkloads } from "./api";
import { type Percentile } from "./Dashboard/ClusterDetail/Components/PercentileSelector";
import { type TimeRange } from "./Dashboard/ClusterDetail/Components/TimeRangeSelector";
import { METRICS } from "./Dashboard/data/metrics";
import { WORKLOAD_USAGE } from "./Dashboard/data/workloadUsage";
import { type K8sUtilizationMetric } from "./Dashboard/types";
import { type DashboardCluster, type DashboardClustersResponse, type DashboardWorkloadsResponse } from "./types";

export interface UseClusterOptions {
  includeDeletedClusters?: boolean;
}

const defaultClusterOptions: UseClusterOptions = {
  includeDeletedClusters: false,
};

export function useCluster(id: Cluster["id"] | null | undefined) {
  const [clusters, loading] = useClusters();
  const cluster = clusters?.find((c) => c.id === id);
  return [cluster as Cluster | undefined, loading] as const;
}

export function useClusters(options: UseClusterOptions = defaultClusterOptions) {
  const { customer } = useCustomerContext();

  let query = getCollection(IntegrationModel)
    .doc("fsk8s")
    .collection("fsk8sClusters")
    .where("customerRef", "==", customer.ref);

  if (query && !options.includeDeletedClusters) {
    query = query.where("discoveryStatus", "==", ClusterDiscoveryStatus.HEALTHY);
  }

  return useCollectionData(query, {
    caching: true,
    cachingKeys: getCachingKeys(customer?.id),
    idField: "id",
  });
}

export function useClustersWithCloudConnect(options: UseClusterOptions = defaultClusterOptions) {
  const { customer } = useCustomerContext({ allowNull: true });
  const [clusters, loading] = useClusters(options);
  const [cachedClusters, setCachedClusters] = useState<WithModel<ClusterWithCloudConnect>[]>([]);
  const [cloudConnectDocs, cloudConnectLoading] = useCloudConnect(customer);

  useEffect(() => {
    if (!clusters) return;

    const fetchCloudConnect = async () => {
      const updatedClusters = clusters.map((cluster: WithModel<ClusterWithCloudConnect>) => {
        if (!cluster?.cloudConnectRef?.path || cluster.cloudConnect) {
          return cluster;
        }

        const matchingCloudConnectDoc = cloudConnectDocs?.find((doc) => doc.id === cluster.cloudConnectRef.id);

        return {
          ...cluster,
          cloudConnect: matchingCloudConnectDoc,
        };
      });

      setCachedClusters(updatedClusters);
    };

    fetchCloudConnect();
  }, [clusters, cloudConnectDocs]);

  return [cachedClusters, loading || cloudConnectLoading] as const;
}

export function useKubernetesFeatures() {
  const { customer } = useCustomerContext({ allowNull: true });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });

  const canCustomerAccessFeature = (feature: KubernetesFeature) => {
    if (isDoitEmployee || !customer || !Array.isArray(customer.earlyAccessFeatures) || !feature.featureFlag) {
      return true;
    }

    return customer.earlyAccessFeatures.includes(feature.featureFlag as EarlyAccessFeature);
  };

  const query = getCollection(IntegrationModel).doc("fsk8s").collection("fsk8sFeatures");

  const [features, loading] = useCollectionData(query, {
    idField: "id",
  });

  const availableFeatures = features?.filter(canCustomerAccessFeature);

  const featureMap: Record<KubernetesFeature["id"], KubernetesFeature> | undefined = availableFeatures?.reduce(
    (acc, feature) => {
      acc[feature?.id] = feature;
      return acc;
    },
    {}
  );

  return [availableFeatures, loading, featureMap] as const;
}

export function useGKEAndCloudConnect() {
  const [hasGKE, setHasGKE] = useState(false);
  const api = useApiContext();
  const { customer } = useCustomerContext();

  const fetchGKECluster = useCallback(async () => {
    const result = await customerHasGKECluster(api, customer.id);
    setHasGKE(Boolean(result.data));
  }, [api, customer.id]);

  useEffect(() => {
    fetchGKECluster();
  }, [fetchGKECluster]);

  const [cloudConnect] = useCloudConnect(customer);
  // Whether the customer has any healthy cloud connect doc, in which case we don't show the banner
  const hasCloudConnect =
    cloudConnect?.some((sa) => sa.cloudPlatform === "google-cloud" && sa.categoriesStatus?.["fsk8s-core"] === 1) ||
    false;

  // Whether the customer has any GCP organization where the fsk8s feature isn't enabled
  const unhealthyOrganization =
    cloudConnect?.find(
      (sa) =>
        sa.cloudPlatform === "google-cloud" && sa.categoriesStatus?.["fsk8s-core"] !== 1 && sa.scope === "organization"
    ) || null;

  return { hasGKE, hasCloudConnect, unhealthyOrganization };
}

export const useClusterMetrics = (clusterId: string, timeRange: TimeRange, percentile: Percentile) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<K8sUtilizationMetric[] | null>(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchClusterMetrics(api, customer.id, clusterId, timeRange, percentile);
      const { data } = response.data;
      const formattedMetrics = typeof data === "string" ? JSON.parse(data) : data;
      setMetrics(formattedMetrics);
    } catch (error) {
      // TODO: Uncomment this after testing
      // errorSnackbar(`Error fetching cluster metrics`);
    } finally {
      setLoading(false);
    }
  }, [clusterId, customer.id, timeRange, percentile, api]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // TODO: Only return real metrics for every case when we are done with testing
  return { metrics: metrics && !loading ? metrics : METRICS, loading };
};

interface UseWorkloadsMetricsProps {
  clusterId: string;
  timeRange: TimeRange;
  percentile: Percentile;
  workloadNames: string[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @eslint-react/hooks-extra/no-useless-custom-hooks
export const useWorkloadsMetrics = ({ clusterId, timeRange, percentile, workloadNames }: UseWorkloadsMetricsProps) => {
  const availableWorkloads = WORKLOAD_USAGE;

  const selectedWorkloads = Object.fromEntries(
    Object.entries(availableWorkloads).filter(([key]) => workloadNames.includes(key))
  );

  return {
    workloads: selectedWorkloads,
  };
};

export const useDashboardClusters = (timeRange?: TimeRange) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();
  const [clusters, clustersLoading] = useClusters();

  const [dashboardClusters, setDashboardClusters] = useState<DashboardClustersResponse | undefined>();
  const [loading, setLoading] = useState(false);

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardClusters(api, customer.id, timeRange);
      setDashboardClusters(response.data);
    } catch (error) {
      errorSnackbar("Error fetching dashboard clusters");
      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
    }
  }, [api, customer.id, errorSnackbar, timeRange]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const enrichedDashboardClusters: DashboardCluster[] = useMemo(
    () =>
      dashboardClusters?.clusters.map((cluster) => ({
        ...cluster,
        doitCluster: clusters?.find((doitCluster) => doitCluster.name === cluster.name),
      })) ?? [],
    [clusters, dashboardClusters]
  );

  return {
    dashboardClusters: {
      ...dashboardClusters,
      clusters: enrichedDashboardClusters,
    },
    loading: loading || clustersLoading,
  };
};

export const useDashboardWorkloads = (clusterId: string, timeRange?: TimeRange) => {
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const errorSnackbar = useErrorSnackbar();

  const [dashboardWorkloads, setDashboardWorkloads] = useState<DashboardWorkloadsResponse>();
  const [loading, setLoading] = useState(false);

  const fetchWorkloads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardWorkloads(api, customer.id, clusterId, timeRange);
      setDashboardWorkloads(response.data);
    } catch (error) {
      errorSnackbar("Error fetching dashboard workloads");
      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
    }
  }, [api, customer.id, errorSnackbar, clusterId, timeRange]);

  useEffect(() => {
    fetchWorkloads();
  }, [fetchWorkloads]);

  return {
    dashboardWorkloads: dashboardWorkloads?.workloads ?? [],
    loading,
  };
};

export const useCustomerHasFsk8sAWSPermissions = () => {
  const [accounts, _, accountsAreLoading] = useAWSAccounts();

  const healthyAccounts = accounts.filter((a) => a.status === AccountStatus.healthy);
  const customerHasFsk8sAWSPermissions = healthyAccounts.some((a) =>
    a.featureNames.includes(FeatureDetails[AWSFeatureName.fsk8s_core].displayName)
  );

  return {
    customerHasFsk8sAWSPermissions,
    loading: accountsAreLoading,
  };
};

export const useCustomerHasFsk8sGCPPermissions = () => {
  const { customer } = useCustomerContext();
  const [cloudConnectData, , isLoaded] = useCloudConnectData(customer.id);

  const customerHasFsk8sGcpPermissions = cloudConnectData.some(
    (cc) => cc.data.categoriesStatus["fsk8s-core"] === CategoryStatus.Healthy
  );

  return {
    customerHasFsk8sGcpPermissions,
    loading: !isLoaded,
  };
};

export const useCustomerHasFsk8sPermissions = () => {
  const { customerHasFsk8sAWSPermissions, loading: awsLoading } = useCustomerHasFsk8sAWSPermissions();
  const { customerHasFsk8sGcpPermissions, loading: gcpLoading } = useCustomerHasFsk8sGCPPermissions();

  return {
    customerHasFsk8sPermissions: customerHasFsk8sAWSPermissions || customerHasFsk8sGcpPermissions,
    loading: awsLoading || gcpLoading,
  };
};

export const useCustomerHasAgentInstalled = () => {
  const [clusters, loading] = useClusters();

  const hasAgentInstalled = clusters?.some((c) => c.perfectScale?.PsClusterID);

  return {
    customerHasAgentInstalled: hasAgentInstalled,
    loading,
  };
};
